import { getJson, sendJson } from './api';

/**
 * The Lead Control Panel (Sales → Lead Control Panel): typed fetchers over
 * /api/v3/lead-panel, plus the lead conversion endpoints (/api/v3/leads/:id).
 * Shapes mirror the API's lead-panel.ts and lead-conversion.ts.
 */

/** Why a lead is disqualified (mirrors the API's DISQUALIFY_REASON). */
export const DISQUALIFY_REASON = {
  spam: 'spam',
  unqualified: 'unqualified',
} as const;
export type DisqualifyReason = (typeof DISQUALIFY_REASON)[keyof typeof DISQUALIFY_REASON];

export const DISQUALIFY_LABEL: Readonly<Record<DisqualifyReason, { readonly verb: string; readonly status: string }>> = {
  [DISQUALIFY_REASON.spam]: { verb: 'Mark as spam', status: 'Spam / Fake' },
  [DISQUALIFY_REASON.unqualified]: { verb: 'Not a fit', status: 'Unqualified' },
};

export interface PanelLead {
  readonly id: string;
  readonly contactName: string;
  readonly company: string | null;
  readonly email: string | null;
  readonly createdAt: string;
  readonly eventDate: string | null;
  readonly eventCity: string | null;
  readonly program: string | null;
  readonly participants: number | null;
  readonly salesforceUrl: string;
  readonly ownerId: string;
  readonly ownerName: string | null;
  readonly status: string | null;
  readonly leadSource: string | null;
  readonly isConverted: boolean;
  readonly spamFlags: ReadonlyArray<{ readonly flag: string; readonly label: string }>;
}

export interface PanelRep {
  readonly id: string;
  readonly name: string;
  readonly openLeads: number;
  readonly openOpportunities: number;
  readonly stages: ReadonlyArray<{ readonly stage: string; readonly count: number }>;
  readonly openTasks: number;
  readonly overdueTasks: number;
  readonly receivedToday: number;
  readonly receivedYesterday: number;
}

export interface LeadPanel {
  readonly queue: readonly PanelLead[];
  readonly reps: readonly PanelRep[];
  /** Stages any rep has open opportunities in, in the org's order. */
  readonly stages: readonly string[];
  readonly recent: readonly PanelLead[];
  readonly today: string;
  readonly yesterday: string;
  /** May the viewer assign and disqualify (the manage_lead_assignment grant)? */
  readonly canManage: boolean;
}

/** What converting a lead will do (POST /leads/:id/conversion-preview). */
export interface ConversionPlan {
  readonly contactName: string;
  readonly email: string | null;
  readonly leadOwner: { readonly id: string; readonly name: string };
  readonly account:
    | {
        readonly mode: 'existing';
        readonly id: string;
        readonly name: string;
        readonly matchedBy: string;
        readonly owner: { readonly name: string };
        readonly newOwner: { readonly name: string } | null;
      }
    | { readonly mode: 'new'; readonly name: string };
  readonly contactId: string | null;
  readonly opportunity: {
    readonly name: string;
    readonly closeDate: string;
    readonly salesperson: string;
    readonly minimumParticipants: number;
    readonly leadSource: string;
  };
  readonly note: { readonly title: string; readonly body: string } | null;
  readonly otherMatches: ReadonlyArray<{ readonly accountId: string; readonly accountName: string; readonly ownerName: string | null }>;
  readonly notes: readonly string[];
}

export interface ConversionDone {
  readonly plan: ConversionPlan;
  readonly opportunityId: string;
  readonly opportunityUrl: string;
  readonly warnings: readonly string[];
}

/** Which account a conversion uses (mirrors the API's account choice). */
export type AccountChoice = { readonly mode: 'match' } | { readonly mode: 'existing'; readonly accountId: string } | { readonly mode: 'new' };

export const LEAD_PANEL_QUERY_KEY = {
  root: ['lead-panel'],
  // Not under root: refreshing the panel after a conversion must not re-preview the converted lead.
  preview: (leadId: string, account: AccountChoice) => ['lead-conversion-preview', leadId, account] as const,
} as const;

/** Polled while the panel is open: new leads arrive all day. */
export const LEAD_PANEL_REFRESH_MS = 60_000;

export function readLeadPanel(signal?: AbortSignal): Promise<LeadPanel> {
  return getJson<LeadPanel>('/lead-panel', undefined, signal);
}

export function assignLead(leadId: string, ownerId: string): Promise<PanelLead> {
  return sendJson<PanelLead>('post', `/lead-panel/leads/${encodeURIComponent(leadId)}/assign`, { ownerId });
}

export function disqualifyLead(leadId: string, reason: DisqualifyReason): Promise<PanelLead> {
  return sendJson<PanelLead>('post', `/lead-panel/leads/${encodeURIComponent(leadId)}/disqualify`, { reason });
}

export function previewConversion(leadId: string, account: AccountChoice): Promise<ConversionPlan> {
  return sendJson<ConversionPlan>('post', `/leads/${encodeURIComponent(leadId)}/conversion-preview`, { account });
}

export function convertLead(leadId: string, account: AccountChoice): Promise<ConversionDone> {
  return sendJson<ConversionDone>('post', `/leads/${encodeURIComponent(leadId)}/convert`, { account });
}

const timeFormat = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
const dayFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' });
const eventDayFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const easternDayFormat = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });

/** "Today 9:42 AM", "Yesterday 4:10 PM", or "Oct 3, 9:42 AM", on Eastern time. */
export function receivedLabel(createdAt: string, panel: Pick<LeadPanel, 'today' | 'yesterday'>): string {
  const instant = new Date(createdAt);
  const day = easternDayFormat.format(instant);
  const time = timeFormat.format(instant);
  if (day === panel.today) return `Today ${time}`;
  if (day === panel.yesterday) return `Yesterday ${time}`;
  return `${dayFormat.format(instant)}, ${time}`;
}

/** A Salesforce date ("2026-11-12") as "Nov 12, 2026". */
export function eventDateLabel(date: string): string {
  return eventDayFormat.format(new Date(`${date.slice(0, 10)}T00:00:00Z`));
}

/** "Nov 12, 2026 · Denver · 30 people", from whatever the inquiry gave. */
export function eventSummary(lead: Pick<PanelLead, 'eventDate' | 'eventCity' | 'participants'>): string {
  return [
    lead.eventDate ? eventDateLabel(lead.eventDate) : null,
    lead.eventCity,
    lead.participants ? `${lead.participants.toLocaleString('en-US')} people` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}
